import { useEffect, useMemo, useState } from "react";
import { Drawer, Modal } from "antd";
import { Link, useSearchParams } from "react-router-dom";
import { CareShell } from "../../components/care/CareLayout";
import DailyNoteList from "../../components/care/DailyNoteList";
import DailyNoteEditorDrawer from "../../components/care/DailyNoteEditorDrawer";
import DailyNoteAttachmentGallery from "../../components/care/DailyNoteAttachmentGallery";
import ResidentInformationSummary from "../../components/care/ResidentInformationSummary";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { careService } from "../../services/careService";
import AssessmentWeeklyReportsPage from "./AssessmentWeeklyReportsPage";

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.content)) return value.content;
  if (Array.isArray(value?.data)) return value.data;
  return [];
};

const residentIdFor = (resident = {}) => resident.id || resident.residentId || resident.clientId || "";
const residentNameFor = (resident = {}) =>
  resident.name || resident.fullName || resident.residentName ||
  [resident.firstName, resident.middleName, resident.lastName].filter(Boolean).join(" ") ||
  "Unnamed resident";
const initialsFor = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "--";
const processIdFor = (process = {}) => process.id || process.assessmentProcessId || process.processId || "";
const processStatusFor = (process = {}) => process.workflowStatus || process.processStatus || process.status || "Active";
const processStartFor = (process = {}) => process.processStartDate || process.startDate || process.assessmentDate || process.createdAt || "";
const processEndFor = (process = {}) => process.processEndDate || process.endDate || process.estimatedEndDate || "";
const processTitleFor = (process = {}) => process.name || process.title || process.processName || process.assessmentName || "Resident assessment";
const dateValue = (value) => {
  const parsed = new Date(value || 0).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const recordTypeFor = (process = {}, resident = {}) => {
  const type = String(process.patientType || process.residentType || resident.patientType || resident.residentType || "").toUpperCase();
  return type === "RESIDENT" ? "annual-residency-progress" : "resident-30-day-probation";
};

const formatDate = (value) => {
  if (!value) return "Not set";
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(String(value)) ? `${value}T12:00:00` : value;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? "Not set" : date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};
const birthMonthYearFor = (resident = {}) => {
  const value = resident.birthDate || resident.dateOfBirth || resident.dob;
  if (!value) return "";
  const match = String(value).match(/^(\d{4})-(\d{2})/);
  if (match) return `${match[2]}/${match[1]}`;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
};
const residentNameWithBirthMonthFor = (resident = {}) => {
  const name = residentNameFor(resident);
  const birthMonthYear = birthMonthYearFor(resident);
  return birthMonthYear ? `${name} - ${birthMonthYear}` : name;
};

const errorMessageFor = (error, fallback) => error?.response?.data?.message || error?.message || fallback;
const dateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
const todayRange = () => {
  const today = dateInputValue(new Date());
  return { startDate: today, endDate: today };
};
const currentWeekRange = () => {
  const today = new Date();
  const monday = new Date(today);
  const day = monday.getDay();
  monday.setDate(monday.getDate() - (day === 0 ? 6 : day - 1));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { startDate: dateInputValue(monday), endDate: dateInputValue(sunday) };
};
const currentMonthRange = () => {
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  return { startDate: dateInputValue(firstDay), endDate: dateInputValue(lastDay) };
};

export default function DailyNotesPage() {
  const { careRoles = [] } = useAuth();
  const { selectedWorkspaceId, workspaceListReady } = useWorkspace();
  const [searchParams, setSearchParams] = useSearchParams();
  const [residents, setResidents] = useState([]);
  const [residentSearch, setResidentSearch] = useState("");
  const [residentsLoading, setResidentsLoading] = useState(true);
  const [residentsError, setResidentsError] = useState("");
  const [assessment, setAssessment] = useState(null);
  const [notes, setNotes] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");
  const [selectedNote, setSelectedNote] = useState(null);
  const [notesRevision, setNotesRevision] = useState(0);
  const [addNoteOpen, setAddNoteOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteRange, setNoteRange] = useState(todayRange);
  const [reportGenerating, setReportGenerating] = useState(false);
  const [reportError, setReportError] = useState("");
  const [reportsDrawerOpen, setReportsDrawerOpen] = useState(false);
  const [reportsRevision, setReportsRevision] = useState(0);
  const selectedResidentId = searchParams.get("residentId") || "";
  const canManageDailyNotes = careRoles.some((role) => {
    const normalized = String(role || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "_");
    return normalized === "care_admin" || normalized === "care_owner";
  });

  useEffect(() => {
    if (!workspaceListReady) return;
    if (!selectedWorkspaceId) {
      setResidents([]);
      setResidentsLoading(false);
      setResidentsError("Select a workspace to view daily notes.");
      return;
    }
    let active = true;
    setResidentsLoading(true);
    careService.residents.listMini({ workspaceId: selectedWorkspaceId, wid: selectedWorkspaceId, size: 100 })
      .then((response) => {
        if (!active) return;
        const nextResidents = asArray(response).filter((resident) => residentIdFor(resident));
        setResidents(nextResidents);
        setResidentsError("");
      })
      .catch((error) => {
        if (!active) return;
        setResidents([]);
        setResidentsError(errorMessageFor(error, "Unable to load residents."));
      })
      .finally(() => active && setResidentsLoading(false));
    return () => { active = false; };
  }, [selectedWorkspaceId, workspaceListReady]);

  const selectedResident = useMemo(
    () => residents.find((resident) => residentIdFor(resident) === selectedResidentId) || null,
    [residents, selectedResidentId],
  );

  useEffect(() => {
    if (!selectedResidentId || !selectedWorkspaceId) {
      setAssessment(null);
      setNotes([]);
      return;
    }
    let active = true;
    setDetailsLoading(true);
    setDetailsError("");
    setAssessment(null);
    setNotes([]);

    careService.dailyNotes.currentAssessment({
      residentId: selectedResidentId,
      workspaceId: selectedWorkspaceId,
      wid: selectedWorkspaceId,
      startDate: noteRange.startDate,
      endDate: noteRange.endDate,
    })
      .then((response) => {
        if (!active) return;
        const currentAssessment = response?.assessment || null;
        setAssessment(currentAssessment);
        setNotes([...asArray(response?.notes)].sort((left, right) =>
          dateValue(right.noteDate || right.date || right.createdAt) - dateValue(left.noteDate || left.date || left.createdAt)));
      })
      .catch((error) => {
        if (!active) return;
        setAssessment(null);
        setNotes([]);
        setDetailsError(errorMessageFor(error, "Unable to load this resident's daily notes."));
      })
      .finally(() => active && setDetailsLoading(false));
    return () => { active = false; };
  }, [noteRange.endDate, noteRange.startDate, notesRevision, selectedResident, selectedResidentId, selectedWorkspaceId]);

  const filteredResidents = useMemo(() => {
    const query = residentSearch.trim().toLowerCase();
    if (!query) return residents;
    return residents.filter((resident) => residentNameFor(resident).toLowerCase().includes(query));
  }, [residentSearch, residents]);

  const selectResident = (residentId) => setSearchParams((currentParams) => {
    const nextParams = new URLSearchParams(currentParams);
    nextParams.set("residentId", residentId);
    return nextParams;
  });
  const assessmentId = processIdFor(assessment || {});
  const assessmentStageHash = recordTypeFor(assessment || {}, selectedResident || {}) === "annual-residency-progress" ? "behavioral-notes" : "30-days-probation";
  const assessmentPath = assessmentId ? `/assessments/${assessmentId}/current` : "";
  const reportRecordType = recordTypeFor(assessment || {}, selectedResident || {});
  const viewGeneratedReports = () => {
    if (assessmentId) setReportsDrawerOpen(true);
  };
  const performReportGeneration = async () => {
    setReportGenerating(true);
    try {
      await careService.dailyNotes.generateWeeklyReport({
        assessmentProcessId: assessmentId,
        residentId: selectedResidentId,
        recordType: reportRecordType,
        weekStart: noteRange.startDate,
        weekEnd: noteRange.endDate,
      });
      setReportsRevision((current) => current + 1);
      viewGeneratedReports();
    } catch (error) {
      setReportError(errorMessageFor(error, "Unable to generate the report."));
    } finally {
      setReportGenerating(false);
    }
  };
  const generateReport = async () => {
    if (reportGenerating) return;
    setReportError("");
    if (!assessmentId || !selectedResidentId) {
      setReportError("Select a resident with an active assessment before generating a report.");
      return;
    }
    if (!noteRange.startDate || !noteRange.endDate || noteRange.endDate < noteRange.startDate) {
      setReportError("Select a valid report date range.");
      return;
    }
    setReportGenerating(true);
    try {
      const existingReports = asArray(await careService.dailyNotes.listWeeklyReports({
        assessmentProcessId: assessmentId,
        recordType: reportRecordType,
      }));
      const reportExists = existingReports.some((report) =>
        String(report?.weekStart || "").slice(0, 10) === noteRange.startDate
        && String(report?.weekEnd || "").slice(0, 10) === noteRange.endDate);
      if (reportExists) {
        viewGeneratedReports();
        return;
      }
    } catch (error) {
      setReportError(errorMessageFor(error, "Unable to check existing reports."));
      return;
    } finally {
      setReportGenerating(false);
    }
    if (!notes.length) {
      setReportError("No daily notes were found in the selected date range.");
      return;
    }
    Modal.confirm({
      title: "Generate report?",
      content: (
        <div className="daily-notes-report-confirmation">
          <p>Create a report for <strong>{residentNameFor(selectedResident)}</strong> from the notes in this selected range?</p>
          <dl>
            <div><dt>From</dt><dd>{formatDate(noteRange.startDate)}</dd></div>
            <div><dt>To</dt><dd>{formatDate(noteRange.endDate)}</dd></div>
            <div><dt>Notes included</dt><dd>{notes.length}</dd></div>
          </dl>
        </div>
      ),
      okText: "Generate Report",
      cancelText: "Cancel",
      onOk: performReportGeneration,
    });
  };
  const editNote = (note) => {
    if (!canManageDailyNotes || !note?.id) return;
    setEditingNote(note);
    setAddNoteOpen(true);
  };
  const deleteNote = (note) => {
    if (!canManageDailyNotes || !note?.id) return;
    Modal.confirm({
      title: "Delete daily note?",
      content: `This permanently removes “${note.title || note.category || "Daily note"}” and its attachments from the resident's active assessment. This action cannot be undone.`,
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Keep note",
      onOk: async () => {
        await careService.dailyNotes.delete(note.id);
        setSelectedNote((current) => current?.id === note.id ? null : current);
        setNotesRevision((current) => current + 1);
      },
    });
  };

  return (
    <CareShell className="dashboard-content app-page-content daily-notes-page">
      <header className="daily-notes-page-header">
        <div>
          <span className="page-eyebrow">Resident Care</span>
          <h1>Daily Notes</h1>
          <p>Review observations recorded during each resident&apos;s current assessment.</p>
        </div>
      </header>

      <div className="daily-notes-workspace">
        <aside className="daily-notes-resident-panel" aria-label="Residents">
          <div className="daily-notes-panel-heading">
            <div>
              <strong>Residents</strong>
              <span>{residents.length} available</span>
            </div>
            <i className="ti ti-users" />
          </div>
          <label className="daily-notes-resident-search">
            <i className="ti ti-search" />
            <input value={residentSearch} onChange={(event) => setResidentSearch(event.target.value)} placeholder="Search residents" />
          </label>
          <div className="daily-notes-resident-list">
            {residentsLoading ? <div className="daily-notes-loading"><i className="ti ti-loader-2" /> Loading residents...</div> : null}
            {residentsError ? <p className="form-error">{residentsError}</p> : null}
            {!residentsLoading && !residentsError && filteredResidents.map((resident) => {
              const id = residentIdFor(resident);
              const name = residentNameFor(resident);
              return (
                <button className={id === selectedResidentId ? "active" : ""} type="button" onClick={() => selectResident(id)} key={id}>
                  <span>{resident.initials || initialsFor(name)}</span>
                  <div><strong>{name}</strong><small>{resident.room || resident.roomNumber ? `Room ${resident.room || resident.roomNumber}` : "Resident"}</small></div>
                  <i className="ti ti-chevron-right" />
                </button>
              );
            })}
            {!residentsLoading && !residentsError && !filteredResidents.length ? <p className="probation-empty">No residents found.</p> : null}
          </div>
        </aside>

        <main className="daily-notes-detail-panel">
          {selectedResident ? <ResidentInformationSummary resident={selectedResident} /> : null}
          {!selectedResident ? (
            <div className="daily-notes-empty-state daily-notes-welcome-state">
              <span className="round-icon blue"><i className="ti ti-notes" /></span>
              <span className="page-eyebrow">Daily Notes</span>
              <h2>Add and manage a resident&apos;s daily behavior and care observations</h2>
              <p>Select a resident to view notes from their current active assessment, actions taken, and supporting attachments.</p>
              <div className="daily-notes-welcome-steps">
                <div><i className="ti ti-user-search" /><span><strong>Choose a resident</strong><small>Use the list or search on the left.</small></span></div>
                <div><i className="ti ti-calendar-range" /><span><strong>Select a date range</strong><small>Review today, this week, this month, or custom dates.</small></span></div>
                <div><i className="ti ti-file-analytics" /><span><strong>Review or report</strong><small>Open note details or generate a report from the selected range.</small></span></div>
              </div>
            </div>
          ) : detailsLoading ? (
            <div className="daily-notes-empty-state"><i className="ti ti-loader-2 daily-notes-spinner" /><h2>Loading daily notes</h2><p>Finding the current assessment and its notes.</p></div>
          ) : detailsError ? (
            <div className="daily-notes-empty-state error"><i className="ti ti-alert-circle" /><h2>Daily notes unavailable</h2><p>{detailsError}</p></div>
          ) : !assessment ? (
            <div className="daily-notes-empty-state"><i className="ti ti-clipboard-off" /><h2>No active assessment</h2><p>{residentNameFor(selectedResident)} does not have a current active assessment.</p></div>
          ) : (
            <>
              <section className="daily-notes-assessment-summary">
                <div className="daily-notes-assessment-title">
                  <span className="round-icon blue"><i className="ti ti-clipboard-text" /></span>
                  <div>
                    <span>Current care process</span>
                    <h2>{processTitleFor(assessment)}</h2>
                    <p>{residentNameWithBirthMonthFor(selectedResident)}</p>
                    <p className="daily-notes-assessment-status"><i className="ti ti-circle-filled" /> Status: {processStatusFor(assessment)}</p>
                  </div>
                  <div className="daily-notes-assessment-actions">
                    <button className="daily-notes-add-button" type="button" onClick={() => setAddNoteOpen(true)}><i className="ti ti-plus" /> Add Daily Note</button>
                    <button className="daily-notes-report-button generate" type="button" disabled={reportGenerating} onClick={generateReport}><i className={`ti ${reportGenerating ? "ti-loader-2" : "ti-file-analytics"}`} /> {reportGenerating ? "Generating..." : "Generate Report"}</button>
                    <button className="daily-notes-report-button view" type="button" onClick={viewGeneratedReports}><i className="ti ti-folder-open" /> View Generated Reports</button>
                    <Link className="daily-notes-assessment-button" to={`${assessmentPath}#${assessmentStageHash}`}><i className="ti ti-external-link" /> Assessment</Link>
                  </div>
                </div>
                {reportError ? <p className="form-error daily-notes-report-error">{reportError}</p> : null}
                <dl>
                  <div><dt>Assessment type</dt><dd>{recordTypeFor(assessment, selectedResident) === "annual-residency-progress" ? "Annual residency progress" : "30-day probation"}</dd></div>
                  <div><dt>Started</dt><dd>{formatDate(processStartFor(assessment))}</dd></div>
                  <div><dt>Expected end</dt><dd>{formatDate(processEndFor(assessment))}</dd></div>
                  <div><dt>Daily notes</dt><dd>{notes.length}</dd></div>
                </dl>
              </section>
              <section className="daily-notes-list-section">
                <div className="daily-notes-list-toolbar">
                  <div className="daily-notes-list-heading"><span className="round-icon green"><i className="ti ti-notes" /></span><div><h2>Notes</h2><p>{notes.length} observation{notes.length === 1 ? "" : "s"} in the selected range</p></div></div>
                  <div className="daily-notes-range-controls">
                    <label><span>From</span><input type="date" value={noteRange.startDate} max={noteRange.endDate} onChange={(event) => setNoteRange((current) => ({ ...current, startDate: event.target.value }))} /></label>
                    <label><span>To</span><input type="date" value={noteRange.endDate} min={noteRange.startDate} onChange={(event) => setNoteRange((current) => ({ ...current, endDate: event.target.value }))} /></label>
                    <button className={noteRange.startDate === todayRange().startDate && noteRange.endDate === todayRange().endDate ? "active" : ""} type="button" title="Show today's notes" onClick={() => setNoteRange(todayRange())}><i className="ti ti-calendar-event" /><span>Today</span></button>
                    <button className={noteRange.startDate === currentWeekRange().startDate && noteRange.endDate === currentWeekRange().endDate ? "active" : ""} type="button" title="Show this week's notes" onClick={() => setNoteRange(currentWeekRange())}><i className="ti ti-calendar-week" /><span>This week</span></button>
                    <button className={noteRange.startDate === currentMonthRange().startDate && noteRange.endDate === currentMonthRange().endDate ? "active" : ""} type="button" title="Show this month's notes" onClick={() => setNoteRange(currentMonthRange())}><i className="ti ti-calendar-month" /><span>This month</span></button>
                  </div>
                </div>
                <DailyNoteList notes={notes} variant="page" onView={setSelectedNote} onEdit={canManageDailyNotes ? editNote : undefined} onDelete={canManageDailyNotes ? deleteNote : undefined} />
              </section>
            </>
          )}
        </main>
      </div>
      <Modal title="Daily Note Details" open={Boolean(selectedNote)} footer={null} width={720} onCancel={() => setSelectedNote(null)}>
        {selectedNote ? <div className="daily-note-details-modal">
          <div className="daily-note-details-heading"><div><span>{formatDate(selectedNote.noteDate || selectedNote.date)}</span><h2>{selectedNote.title || "Daily note"}</h2></div>{selectedNote.category ? <em>{selectedNote.category}</em> : null}</div>
          <section><span>Description</span><p>{String(selectedNote.summary || selectedNote.notes || "No description added.").replace(/<[^>]+>/g, " ")}</p></section>
          <section><span>Action taken</span><p>{String(selectedNote.interventionAction || selectedNote.intervention || selectedNote.actionTaken || "No action recorded.").replace(/<[^>]+>/g, " ")}</p></section>
          {Array.isArray(selectedNote.photos) && selectedNote.photos.length ? <div className="daily-note-attachments"><span>Attachments</span><DailyNoteAttachmentGallery attachments={selectedNote.photos} /></div> : null}
          <div className="daily-note-details-footer"><span><i className="ti ti-user" /> {selectedNote.authorName || selectedNote.createdByName || "Care staff"}</span><span><i className="ti ti-clock" /> {selectedNote.rangeStartTimestamp ? new Date(selectedNote.rangeStartTimestamp).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "Time not recorded"}</span>{selectedNote.updatedAt && selectedNote.updatedAt !== selectedNote.createdAt ? <><span><i className="ti ti-edit" /> Updated by {selectedNote.updatedByName || selectedNote.updatedBy || "Care staff"}</span><span><i className="ti ti-history" /> {new Date(selectedNote.updatedAt).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}</span></> : null}</div>
        </div> : null}
      </Modal>
      <DailyNoteEditorDrawer
        assessment={assessment}
        resident={selectedResident}
        recordType={recordTypeFor(assessment || {}, selectedResident || {})}
        workspaceId={selectedWorkspaceId}
        editingNote={editingNote}
        open={addNoteOpen}
        onClose={() => { setAddNoteOpen(false); setEditingNote(null); }}
        onSaved={() => { setSelectedNote(null); setNotesRevision((current) => current + 1); }}
      />
      <Drawer
        className="generated-reports-drawer"
        title="Generated Reports"
        open={reportsDrawerOpen}
        onClose={() => setReportsDrawerOpen(false)}
        width="min(1120px, 96vw)"
        destroyOnClose
      >
        {reportsDrawerOpen ? (
          <AssessmentWeeklyReportsPage
            key={`${assessmentId}-${reportsRevision}`}
            assessmentId={assessmentId}
            recordType={reportRecordType}
            embedded
          />
        ) : null}
      </Drawer>
    </CareShell>
  );
}
